import { guuid, isFunction, isNeArray, isNumber, isObject, isString } from '@aretheyhappy/utils';
import I18N from '../I18N';
/**
 * Enum for sort types
 * @readonly
 * @enum {string}
 */
var TABLE_SORT_TYPES;
(function (TABLE_SORT_TYPES) {
    TABLE_SORT_TYPES["NUMERIC"] = "numeric";
    TABLE_SORT_TYPES["ALPHABETIC"] = "alphabetic";
    TABLE_SORT_TYPES["NUMERIC_UNIT"] = "numberic_unit";
})(TABLE_SORT_TYPES || (TABLE_SORT_TYPES = {}));
;
/**
 * Enum for column types
 * @readonly
 * @enum {string}
 */
var TABLE_COLUMN_TYPES;
(function (TABLE_COLUMN_TYPES) {
    TABLE_COLUMN_TYPES["TITLE"] = "entity-title";
    TABLE_COLUMN_TYPES["BADGE"] = "badge";
    TABLE_COLUMN_TYPES["NUMBER"] = "number";
    TABLE_COLUMN_TYPES["NUMBER_BASIC"] = "number_basic";
    TABLE_COLUMN_TYPES["TEXT"] = "text";
    TABLE_COLUMN_TYPES["BREAKDOWN"] = "breakdown";
    TABLE_COLUMN_TYPES["DROPDOWN"] = "dropdown";
    TABLE_COLUMN_TYPES["CHECKBOX"] = "checkbox";
    TABLE_COLUMN_TYPES["BUTTON"] = "button";
})(TABLE_COLUMN_TYPES || (TABLE_COLUMN_TYPES = {}));
;
/**
 * Table headers that span multiple headers and appear above the main headers.
 * It will be up to the consumer to set the right width for these
 * so that they align cleanly with TableFields
 */
class TableSuperHeader {
    constructor(title = '', width_rem = 10, width_rem_mobile = 10, locked = false) {
        this.uid = guuid();
        // If this header is hidden on mobile
        this.nomobile = false;
        // Title for the column
        this.title = '';
        // Width size for this column in rem
        this.width_rem = 10;
        // Width size for this column in mobile
        this.width_rem_mobile = 10;
        // Number of children this header covers. Used for spacing issues.
        this.child_count = 1;
        // If column should be locked
        this.locked = false;
        this.title = title;
        this.setRemWidth(width_rem);
        this.setRemWidthMobile(width_rem_mobile);
        // If column should be locked
        this.locked = locked;
    }
    setNoMobile(value) { this.nomobile = value; return this; }
    setTitle(value) { this.title = value; return this; }
    setChildCount(value) { this.child_count = value; return this; }
    /**
     * Set the rem width of this column. Must be a number.
     * Default is 10rem. Cannot be smaller than 7.
     * @param {number} value
     */
    setRemWidth(value) {
        if (!isNumber(value)) {
            console.warn(`setRemWidth requires a number to be sent. ${value} was recieved.`);
            return this;
        }
        if (value < 7) {
            console.warn(`setRemWidth value cannot be less than 7. ${value} was recieved.`);
            return this;
        }
        this.width_rem = value;
        return this;
    }
    /**
     * Set the rem width of this column when in mobile view.
     * Usually this will be the same as the normal width
     * unless headers under this dissappear when in mobile.
     * Must be a number. Default is 10rem. Cannot be smaller than 7.
     * @param {number} value
     */
    setRemWidthMobile(value) {
        if (!isNumber(value)) {
            console.warn(`setRemWidthMobile requires a number to be sent. ${value} was recieved.`);
            return this;
        }
        if (value < 7) {
            console.warn(`setRemWidthMobile value cannot be less than 7. ${value} was recieved.`);
            return this;
        }
        this.width_rem_mobile = value;
        return this;
    }
}
/**
 * TableField is used to configure a column for Table.vue
 */
class TableField {
    constructor(key = '') {
        this.uid = guuid();
        // Unique key for this column
        this.key = '';
        // If this header is hidden on mobile
        this.nomobile = false;
        // If this column can be sorted
        this.sort_enabled = true;
        // Type of sort for this column
        this.sort_type = TABLE_SORT_TYPES.NUMERIC;
        // If sort is currently active
        this.sort_active = false;
        // Data type for the column
        this.type = TABLE_COLUMN_TYPES.NUMBER;
        // Title for the column
        this.title = '';
        // Description for the column (shown as tooltip on hover)
        this.description = '';
        // Width size for this column in rem
        this.width_rem = 10;
        // Height size for the column in rem
        this.height_rem = false;
        // Visuals that need to be shown in the header
        this.visual = false;
        // Props for the visual component
        this.visual_props = false;
        // If this column should be locked
        this.locked = false;
        // Custom classes for the text div
        this.text_classes = '';
        // Custom UI options
        this.ui_options = {};
        // Info Description
        this.info_desc = false;
        // Badge Text
        this.badge_text = '';
        this.custom_value_fn = undefined;
        // Class internal values
        this.value_prop_name = '';
        this.comparison_prop_name = '';
        this.fallback = '';
        this.total_prop_name = '';
        this.subvalue_prop_name = '';
        this.disabled_prop_name = '';
        this.intermediate_prop_name = '';
        // Show the current-previous difference value in the number visual
        this.comparison_as_sub = false;
        // If numerical comparison should be inverse
        this.comparison_inverse = false;
        // Checkbox size (for checkbox type columns)
        this.checkbox_size = 's';
        // Content alignment for this column ('left', 'center', 'right')
        this.align = 'left';
        this.key = key;
    }
    setKey(value) { this.key = value; return this; }
    setNoMobile(value) { this.nomobile = value; return this; }
    setSortEnabled(value) { this.sort_enabled = value; return this; }
    setSortType(value) { this.sort_type = value; return this; }
    setSortActive(value) { this.sort_active = value; return this; }
    setType(value) { this.type = value; return this; }
    setTitle(value) { this.title = value; return this; }
    setDescription(value) { this.description = value; return this; }
    setLocked(value) { this.locked = value; return this; }
    setTextClasses(value) { this.text_classes = value; return this; }
    setInfoDescription(value) { this.info_desc = value; return this; }
    setAlign(value) { this.align = value; return this; }
    setBadgeText(value) { this.badge_text = value; return this; }
    /**
     * Set the rem width of this column. Must be a number.
     * Default is 10rem. Cannot be smaller than 7.
     * @param {number} value
     */
    setRemWidth(value) {
        if (!isNumber(value)) {
            console.warn(`setRemWidth requires a number to be sent. ${value} was recieved.`);
            return this;
        }
        if (value < 7) {
            console.warn(`setRemWidth value cannot be less than 7. ${value} was recieved.`);
            return this;
        }
        this.width_rem = value;
        return this;
    }
    /**
     * Set the rem height of this column. Must be a number.
     * Default is false and in that case would be set to 100%.
     * @param {number} value
     */
    setRemHeight(value) {
        if (!isNumber(value)) {
            console.warn(`setRemHeight requires a number to be sent. ${value} was recieved.`);
            return this;
        }
        this.height_rem = value;
        return this;
    }
    /**
     * Overwrite the value coputation for this column
     * @param {function} value - a function that will compute the cell value
     * @returns
     */
    setCustomValue(value) {
        if (!isFunction(value)) {
            console.error('setCustomValue requires a function as value');
            return this;
        }
        this.custom_value_fn = value;
        return this;
    }
    /**
     * Sets column cell display type to title
     * @param {string} propName - Property name on the data object to use for the cell display string
     */
    setColumnTypeTitle(propName) {
        this.type = TABLE_COLUMN_TYPES.TITLE;
        this.value_prop_name = propName;
        return this;
    }
    /**
     * Sets column cell display type to text
     * @param {string} propName - Property name on the data object to use for the cell display string
     */
    setColumnTypeText(propName) {
        this.type = TABLE_COLUMN_TYPES.TEXT;
        this.value_prop_name = propName;
        return this;
    }
    setColumnTypeBadge() {
        this.type = TABLE_COLUMN_TYPES.BADGE;
        return this;
    }
    setColumnTypeDropdown(options = {}) {
        this.type = TABLE_COLUMN_TYPES.DROPDOWN;
        this.ui_options = Object.assign({
            uiBtnSize: 's',
            uiBtnDisplay: 'icon',
            uiBtnIcon: 'ellipsis',
        }, isObject(options) ? options : {});
        return this;
    }
    /**
     * Set column cell display type to numeric
     * @param {string} value_prop_name - property name on the data object for the main value
     * @param {string} comparison_prop_name - property name on the data object for the comparison value
     * @param {string} fallback - fallback string to show if no value present (if undefined, uses 'N/A')
     * @param {string} total_prop_name - property name on the data object for the total value
     * @param {boolean} comparison_as_sub - flag if the comparison value should be shown as a subvalue
     * @param {boolean} comparison_inverse - flag if the comparison should be done inverse (for rank where going from 1->2 is a downgrade)
     * @param {string} subvalue_prop_name - property name on the data object for the sub value
     */
    setColumnTypeNumber(value_prop_name, comparison_prop_name, fallback, total_prop_name, comparison_as_sub = false, comparison_inverse = false, subvalue_prop_name = '') {
        if (!isString(value_prop_name) || value_prop_name.length === 0) {
            console.error('value_prop_name is required to be a nonempty string');
        }
        this.type = TABLE_COLUMN_TYPES.NUMBER;
        this.value_prop_name = value_prop_name;
        this.comparison_prop_name = comparison_prop_name;
        this.fallback = fallback;
        this.total_prop_name = total_prop_name;
        this.subvalue_prop_name = subvalue_prop_name;
        this.comparison_as_sub = comparison_as_sub;
        this.comparison_inverse = comparison_inverse;
        return this;
    }
    /**
     * Set a column cell to display a basic number type
     * @param {string} value_prop_name
     * @param {string} fallback
     * @param {string} trend
     */
    setColumnTypeNumberBasic(value_prop_name, fallback, trend) {
        this.type = TABLE_COLUMN_TYPES.NUMBER_BASIC;
        this.value_prop_name = value_prop_name;
        this.fallback = fallback;
        this.comparison_prop_name = trend;
        return this;
    }
    /**
     * Set column cell display type to breakdown
     * @param {string} value_prop_name - property name on the data object for the main value
     * @returns
     */
    setColumnTypeBreakdown(value_prop_name) {
        this.type = TABLE_COLUMN_TYPES.BREAKDOWN;
        this.value_prop_name = value_prop_name;
        return this;
    }
    /**
     * Set column cell display type to checkbox
     * @param {string} value_prop_name - property name on the data object for the checked state (boolean)
     * @param {string} disabled_prop_name - property name on the data object for disabled state (optional)
     * @param {string} intermediate_prop_name - property name on the data object for intermediate state (optional)
     * @param {string} size - size of the checkbox ('s', 'm', 'l') - defaults to 's'
     * @returns
     */
    setColumnTypeCheckbox(value_prop_name, disabled_prop_name = '', intermediate_prop_name = '', size = 's') {
        this.type = TABLE_COLUMN_TYPES.CHECKBOX;
        this.value_prop_name = value_prop_name;
        this.disabled_prop_name = disabled_prop_name;
        this.intermediate_prop_name = intermediate_prop_name;
        this.checkbox_size = size;
        return this;
    }
    /**
     * Set column cell display type to button
     * @param {TableRowUIElementOptions} options - button options (uiBtnIcon, uiBtnSize, uiBtnDisplay, lbl, disabled)
     * @returns
     */
    setColumnTypeButton(options = {}) {
        this.type = TABLE_COLUMN_TYPES.BUTTON;
        this.ui_options = Object.assign({
            icon: 'minus',
        }, isObject(options) ? options : {});
        return this;
    }
    /**
     * Set checkbox size for checkbox type columns
     * @param {string} size - size of the checkbox ('s', 'm', 'l')
     * @returns
     */
    setCheckboxSize(size) {
        this.checkbox_size = size;
        return this;
    }
    /**
     * Set configs for the visual component that needs to by displayed in the header cells
     * @param {string} type - visual type (icon/image/dot)
     * @param {object} props - properties needed for the visual component
     * @returns
     */
    setVisualProps(type, props) {
        this.visual = type;
        this.visual_props = props;
        return this;
    }
    /**
     * Calculates display value data for this cell.
     *
     * This function gets sent the object data for a cell and
     * changes it to another format for the cells display type to render.
     *
     * For example ath-visual-number expects this format {value: 1, comparison: 2, fallback: 'N/A'}.
     * And the data format may look like {nps: 1, pre_nps: 2}.
     *
     * This function will convert that data format to the other one.
     *
     * Data is sent to the underlying component via v-bind
     *
     * @param {TableRowObject} el - Data object for this cell
     * @returns
     */
    value(el) {
        // If we have a custom computation function, use that instead
        if (this.custom_value_fn !== undefined)
            return this.custom_value_fn(el);
        switch (this.type) {
            // Uses ath-visual-entity-title
            case TABLE_COLUMN_TYPES.TITLE:
                return { name: el[this.value_prop_name] };
            // Uses ath-visual-number
            case TABLE_COLUMN_TYPES.NUMBER:
                return {
                    value: el[this.value_prop_name],
                    comparison: this.comparison_prop_name !== undefined ? el[this.comparison_prop_name] : undefined,
                    fallback: this.fallback === undefined ? I18N.go('general.n_a') : this.fallback,
                    total: el[this.total_prop_name],
                    subvalue: el[this.subvalue_prop_name],
                    comparisonAsSub: this.comparison_as_sub,
                    comparisonInverse: this.comparison_inverse,
                };
            // Uses Visual Number Basic
            case TABLE_COLUMN_TYPES.NUMBER_BASIC:
                return {
                    value: el[this.value_prop_name],
                    trend: this.comparison_prop_name !== undefined ? el[this.comparison_prop_name] : false,
                    fallback: this.fallback === undefined ? I18N.go('general.n_a') : this.fallback,
                };
            // Uses ath-visual-text
            case TABLE_COLUMN_TYPES.TEXT:
                return {
                    text: el[this.value_prop_name] !== undefined ? el[this.value_prop_name] : I18N.go('general.n_a'),
                };
            // Uses ath-visual-breakdown
            case TABLE_COLUMN_TYPES.BREAKDOWN:
                return {
                    value: el[this.value_prop_name],
                };
            // Uses ath-visual-badge
            case TABLE_COLUMN_TYPES.BADGE:
                console.warn('TABLE_COLUMN_TYPES.BADGE default flow is not implemented');
                // I have no idea how to make this one generic
                break;
            // Uses AthButtonIcon
            case TABLE_COLUMN_TYPES.BUTTON:
                return {};
            // Uses ath-form-checkbox
            case TABLE_COLUMN_TYPES.CHECKBOX:
                return {
                    value: el[this.value_prop_name] || false,
                    disabled: this.disabled_prop_name ? (el[this.disabled_prop_name] || false) : false,
                    intermediate: this.intermediate_prop_name ? (el[this.intermediate_prop_name] || false) : false,
                    size: this.checkbox_size || 's',
                };
            default:
                break;
        }
        return {};
    }
}
/**
 * TableRow is used to configure a data row for Table.vue
 */
class TableRow {
    constructor(data) {
        this.config = {
            clickable: true,
            start_expanded: true,
            dropdown_options: []
        };
        Object.assign(this, data);
    }
    setClickable(value) { this.config.clickable = value; return this; }
    setStartExpanded(value) { this.config.start_expanded = value; return this; }
    /**
     * Set dropdown options for this row.
     *
     * Should be an array of objects of the following format
     *
     * {
     *    lbl: String,  // Required
     *    icon: String, // Optional linear icon name
     *
     *    // You can add anything else here and it will be sent back to you on item click
     * }
     * @param {Array<Object>} value
     * @returns
     */
    setDropdownOptions(value) {
        if (!isNeArray(value)) {
            console.warn('Dropdown options must be an non empty array');
            return this;
        }
        this.config.dropdown_options = value;
        return this;
    }
}
export { TABLE_SORT_TYPES, TABLE_COLUMN_TYPES, TableRow, TableField, TableSuperHeader };
//# sourceMappingURL=TableUtils.js.map